import React, { useState, useEffect } from 'react';
import { MapPin, Navigation, Shield, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';

// Local storage key for permission request
const GPS_PERMISSION_ASKED_KEY = 'outsiders_gps_permission_asked';
const GPS_PERMISSION_DENIED_KEY = 'outsiders_gps_permission_denied';

export default function GPSPermissionRequest({ onPermissionGranted, onPermissionDenied, forceShow = false }) {
  const [showDialog, setShowDialog] = useState(false);
  const [permissionState, setPermissionState] = useState('unknown'); // 'unknown', 'prompt', 'granted', 'denied'
  const [isRequesting, setIsRequesting] = useState(false);

  useEffect(() => {
    checkPermissionState();
  }, [forceShow]);

  const checkPermissionState = async () => {
    // Check if browser supports geolocation
    if (!navigator.geolocation) {
      setPermissionState('denied');
      return;
    }

    // Check permission status using Permissions API (if available)
    if ('permissions' in navigator) {
      try {
        const result = await navigator.permissions.query({ name: 'geolocation' });
        setPermissionState(result.state);
        
        // Listen for permission changes
        result.onchange = () => {
          setPermissionState(result.state);
          if (result.state === 'granted' && onPermissionGranted) {
            onPermissionGranted();
          }
        };

        // Show dialog if permission is 'prompt' and hasn't been asked before
        const wasAsked = localStorage.getItem(GPS_PERMISSION_ASKED_KEY);
        const wasDenied = localStorage.getItem(GPS_PERMISSION_DENIED_KEY);
        
        if (result.state === 'prompt' && (!wasAsked || forceShow)) {
          setShowDialog(true);
        } else if (result.state === 'denied' && !wasDenied) {
          setShowDialog(true);
        } else if (result.state === 'granted' && onPermissionGranted) {
          onPermissionGranted();
        }
      } catch (error) {
        // Permissions API not fully supported, try direct geolocation
        const wasAsked = localStorage.getItem(GPS_PERMISSION_ASKED_KEY);
        if (!wasAsked || forceShow) {
          setShowDialog(true);
        }
      }
    } else {
      // Fallback for browsers without Permissions API
      const wasAsked = localStorage.getItem(GPS_PERMISSION_ASKED_KEY);
      if (!wasAsked || forceShow) {
        setShowDialog(true);
      }
    }
  };

  const requestPermission = async () => {
    setIsRequesting(true);
    localStorage.setItem(GPS_PERMISSION_ASKED_KEY, 'true');

    try {
      // Request geolocation - this will trigger the browser's permission prompt
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        });
      });

      // Permission granted!
      setPermissionState('granted');
      localStorage.removeItem(GPS_PERMISSION_DENIED_KEY);
      setShowDialog(false);
      
      if (onPermissionGranted) {
        onPermissionGranted(position);
      }
    } catch (error) {
      // Permission denied or error
      setPermissionState('denied');
      localStorage.setItem(GPS_PERMISSION_DENIED_KEY, 'true');
      
      if (onPermissionDenied) {
        onPermissionDenied(error);
      }
    } finally {
      setIsRequesting(false);
    }
  };

  const handleSkip = () => {
    localStorage.setItem(GPS_PERMISSION_ASKED_KEY, 'true');
    setShowDialog(false);
    
    if (onPermissionDenied) {
      onPermissionDenied({ code: 0, message: 'User skipped' });
    }
  };

  const handleOpenSettings = () => {
    // Provide instructions for enabling GPS
    alert(
      'To enable GPS location:\n\n' +
      '1. Click the lock/info icon in your browser\'s address bar\n' +
      '2. Find "Location" in the permissions list\n' +
      '3. Change it to "Allow"\n' +
      '4. Refresh the page\n\n' +
      'On mobile:\n' +
      '• iOS: Settings > Safari > Location > Allow\n' +
      '• Android: Settings > Site Settings > Location > Allow'
    );
  };

  if (!showDialog) return null;

  return (
    <Dialog open={showDialog} onOpenChange={(open) => !isRequesting && setShowDialog(open)}>
      <DialogContent className="sm:max-w-md mx-4">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-emerald-600">
            <Navigation className="h-5 w-5" />
            Enable GPS Location
          </DialogTitle>
          <DialogDescription>
            Location access helps provide better service
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {/* Benefits of GPS */}
          <div className="space-y-3 mb-4">
            <div className="flex items-start gap-3 p-3 bg-emerald-50 rounded-xl">
              <MapPin className="h-5 w-5 text-emerald-600 mt-0.5" />
              <div>
                <p className="font-medium text-emerald-800 text-sm">Accurate Ride Tracking</p>
                <p className="text-emerald-600 text-xs">Verify arrival at pickup and dropoff locations</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-xl">
              <Navigation className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <p className="font-medium text-blue-800 text-sm">Better Navigation</p>
                <p className="text-blue-600 text-xs">Get directions and real-time location updates</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl">
              <Shield className="h-5 w-5 text-slate-600 mt-0.5" />
              <div>
                <p className="font-medium text-slate-800 text-sm">Your Privacy</p>
                <p className="text-slate-600 text-xs">Location is only used while using the app</p>
              </div>
            </div>
          </div>

          {permissionState === 'denied' && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4">
              <p className="text-amber-800 text-sm">
                Location permission was previously denied. You can enable it in your browser settings.
              </p>
              <Button
                variant="link"
                onClick={handleOpenSettings}
                className="text-amber-700 underline p-0 h-auto mt-1 text-sm"
              >
                How to enable location
              </Button>
            </div>
          )}
        </div>

        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={handleSkip}
            className="flex-1 h-12 rounded-xl"
            disabled={isRequesting}
          >
            <X className="h-4 w-4 mr-2" />
            Skip
          </Button>
          <Button
            onClick={requestPermission}
            className="flex-1 h-12 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
            disabled={isRequesting}
          >
            {isRequesting ? (
              <>
                <div className="h-4 w-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Requesting...
              </>
            ) : (
              <>
                <MapPin className="h-4 w-4 mr-2" />
                Enable GPS
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
